import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { CSSProperties } from 'react';
import { ChevronLeft, ChevronRight, X, Check, Lock, Hourglass, AlertTriangle, Calendar, Eye, LayoutGrid, CalendarCheck, CalendarClock } from 'lucide-react';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDraggable, useDroppable,
} from '@dnd-kit/core';
import type { CollisionDetection, DragStartEvent, DragOverEvent, DragMoveEvent, DragEndEvent } from '@dnd-kit/core';
import { api } from '../api/client';
import { TaskDetailModal } from '../components/TaskDetailModal';
import { ProjectOverviewModal } from '../components/ProjectOverviewModal';
import { SearchableSelect } from '../components/SearchableSelect';
import type { PlannerTask, OutlookEvent, CalendarSlot } from '../types';
import { useT } from '../i18n';
import { taskDisplayCode } from '../utils/identifiers';
import { dayNum, localYmd, shortDay } from '../proposals/timeline-taches/dates';

// ── My calendar ──
// The person's work week (Mon–Fri, 07:00–19:00). Their open tasks are listed
// under the week; dragging one into the week plans a work session — an
// instance: the task stays in the list and is not changed, so the same task
// can be planned as many times as needed. Sessions are moved by dragging,
// resized from their bottom edge and removed with ×. Outlook meetings are a
// read-only overlay. Due dates within a week, and overdue ones, stand out.

type TFn = (key: string, params?: Record<string, string | number>) => string;

// ── Overlap layout ──
// Groups events in the same day column that collide in time and assigns
// each a slot index + total-slots so they can be rendered side by side.
interface LayoutEvent { id: string; startMs: number; endMs: number; }
interface EventLayout { col: number; numCols: number; }

function layoutDay(events: LayoutEvent[]): Map<string, EventLayout> {
  const result = new Map<string, EventLayout>();
  if (events.length === 0) return result;
  const sorted = [...events].sort((a, b) => a.startMs - b.startMs);
  const colEnds: number[] = [];
  const eventCols: number[] = [];
  for (const ev of sorted) {
    let col = colEnds.findIndex(end => end <= ev.startMs);
    if (col === -1) { col = colEnds.length; colEnds.push(0); }
    colEnds[col] = ev.endMs;
    eventCols.push(col);
  }
  // numCols per event = max col among all events it overlaps + 1
  const eventNumCols = eventCols.map((_, i) => {
    let max = eventCols[i];
    for (let j = 0; j < sorted.length; j++) {
      if (i === j) continue;
      if (sorted[i].startMs < sorted[j].endMs && sorted[j].startMs < sorted[i].endMs) {
        max = Math.max(max, eventCols[j]);
      }
    }
    return max + 1;
  });
  sorted.forEach((ev, i) => result.set(ev.id, { col: eventCols[i], numCols: eventNumCols[i] }));
  return result;
}

function blockStyle(layout: EventLayout | undefined, top: number, height: number) {
  if (!layout || layout.numCols === 1) return { top, height };
  const { col, numCols } = layout;
  return {
    top,
    height,
    left: `calc(${col} / ${numCols} * 100% + 3px)`,
    right: `calc(${numCols - col - 1} / ${numCols} * 100% + 3px)`,
  };
}

// ── Grid geometry ──
// Half-hour drop cells; sessions and Outlook meetings are absolute blocks over them.
const START_HOUR = 7;        // first visible hour (inclusive)
const END_HOUR = 19;         // last visible hour (exclusive) → 07:00–19:00
const HOUR_H = 52;           // px per hour
const STEP = 30;             // minutes per drop cell / resize step
const STEP_H = (HOUR_H * STEP) / 60;
const DAY_START = START_HOUR * 60;
const DAY_END = END_HOUR * 60;
const GRID_H = (END_HOUR - START_HOUR) * HOUR_H;
const DEFAULT_MINUTES = 60;
const DAY_COUNT = 5;
const DAY_KEYS = ['calendar.day.mon', 'calendar.day.tue', 'calendar.day.wed', 'calendar.day.thu', 'calendar.day.fri'];
const MONTH_KEYS = [
  'calendar.month.jan', 'calendar.month.feb', 'calendar.month.mar', 'calendar.month.apr',
  'calendar.month.may', 'calendar.month.jun', 'calendar.month.jul', 'calendar.month.aug',
  'calendar.month.sep', 'calendar.month.oct', 'calendar.month.nov', 'calendar.month.dec',
];
const CELL_MINUTES: number[] = [];
for (let m = DAY_START; m < DAY_END; m += STEP) CELL_MINUTES.push(m);
const HOURS: number[] = [];
for (let h = START_HOUR; h < END_HOUR; h++) HOURS.push(h);

// Composite identity — project task #5 and kanban card #5 are distinct items.
const keyOf = (t: { source: string; id: number }) => `${t.source}-${t.id}`;
const slotItemKey = (s: CalendarSlot) => `${s.source}-${s.item_id}`;

function startOfWeek(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0 Sun … 6 Sat
  const diff = day === 0 ? -6 : 1 - day; // back to Monday
  x.setDate(x.getDate() + diff);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

/** Minutes since local midnight. */
const minuteOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes();
const topOf = (minute: number) => ((minute - DAY_START) / 60) * HOUR_H;
const heightOf = (startMinute: number, minutes: number) =>
  Math.max(STEP_H - 2, (Math.min(minutes, DAY_END - startMinute) / 60) * HOUR_H - 2);
const hhmm = (minute: number) => `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

/** ISO 8601 week number. */
function isoWeek(d: Date): number {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = x.getUTCDay() || 7;
  x.setUTCDate(x.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil(((x.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function fmtRange(monday: Date, t: TFn): string {
  const fri = addDays(monday, DAY_COUNT - 1);
  const mLabel = (m: number) => t(MONTH_KEYS[m]);
  if (monday.getMonth() === fri.getMonth()) {
    return `${monday.getDate()}–${fri.getDate()} ${mLabel(fri.getMonth())} ${fri.getFullYear()}`;
  }
  return `${monday.getDate()} ${mLabel(monday.getMonth())} – ${fri.getDate()} ${mLabel(fri.getMonth())} ${fri.getFullYear()}`;
}

// ── Due dates: overdue, or due in less than a week (today included) ──
type DueState = 'overdue' | 'soon' | 'later';
// Static keys so the i18n checker can resolve them.
const GROUP_KEY: Record<DueState, string> = {
  overdue: 'calendar.group.overdue', soon: 'calendar.group.soon', later: 'calendar.group.later',
};

function dueState(due: string | null, today: number): DueState {
  if (!due) return 'later';
  const days = dayNum(due) - today;
  return days < 0 ? 'overdue' : days < 7 ? 'soon' : 'later';
}

function dueLabel(due: string, today: number, t: TFn): string {
  const n = dayNum(due);
  const days = n - today;
  const date = shortDay(n, t);
  if (days < 0) return t('calendar.dueOverdue', { date });
  if (days === 0) return t('calendar.dueToday');
  if (days === 1) return t('calendar.dueTomorrow');
  if (days < 7) return t('calendar.dueInDays', { n: days, date });
  return t('calendar.due', { date });
}

// Due date as on the Kanban card (calendar icon + short date); the full wording
// ("due in 3 days · Tue 17") is the tooltip. Red once overdue, orange when due
// within the week, like the rest of this page.
function DueDate({ due, today }: { due: string | null; today: number }) {
  const { t } = useT();
  if (!due) return null;
  const state = dueState(due, today);
  return (
    <span className={`task-card-due${state === 'overdue' ? ' task-card-due--overdue' : state === 'soon' ? ' task-card-due--soon' : ''}`} title={dueLabel(due, today, t)}>
      <Calendar size={12} />
      {new Date(due).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
    </span>
  );
}

// Static keys so the i18n checker can resolve them.
const OUTLOOK_KEY: Record<NonNullable<CalendarSlot['outlook']>, string> = {
  synced: 'calendar.outlookSynced', failed: 'calendar.outlookFailed',
};

// The project code in front of a title, on cards and sessions alike.
function Code({ code }: { code: string | null }) {
  return code ? <span className="mycal-code">{code}</span> : null;
}

// ── Drag payloads ──
type DragData =
  | { kind: 'tray'; task: PlannerTask }
  | { kind: 'move'; slot: CalendarSlot }
  | { kind: 'resize'; slot: CalendarSlot };

const cellId = (day: number, minute: number) => `cell:${day}:${minute}`;
function parseCell(id: unknown): { day: number; minute: number } | null {
  const m = /^cell:(\d):(\d+)$/.exec(String(id ?? ''));
  return m ? { day: Number(m[1]), minute: Number(m[2]) } : null;
}

// A task from the list lands where the pointer is; a moved session lands where
// its top edge is (snapped to the nearest half hour), so it doesn't jump by
// the offset at which it was grabbed. Resizing doesn't target a cell.
const collision: CollisionDetection = ({ active, collisionRect, droppableRects, droppableContainers, pointerCoordinates }) => {
  const kind = (active.data.current as DragData | undefined)?.kind;
  if (kind === 'resize') return [];
  const point = kind === 'move'
    ? { x: collisionRect.left + collisionRect.width / 2, y: collisionRect.top + STEP_H / 2 }
    : pointerCoordinates;
  if (!point) return [];
  for (const container of droppableContainers) {
    const r = droppableRects.get(container.id);
    if (r && point.x >= r.left && point.x < r.right && point.y >= r.top && point.y < r.bottom) {
      return [{ id: container.id, data: { droppableContainer: container, value: 0 } }];
    }
  }
  return [];
};

// ── Half-hour drop cell ──
function Cell({ day, minute }: { day: number; minute: number }) {
  const { setNodeRef } = useDroppable({ id: cellId(day, minute) });
  return (
    <div
      ref={setNodeRef}
      className={`mycal-cell${minute % 60 === 0 ? ' is-hour' : ''}`}
      style={{ top: topOf(minute), height: STEP_H }}
    />
  );
}

// ── A planned work session (draggable, resizable from its bottom edge) ──
function SessionBlock({ slot, minutes, today, style, onOpen, onRemove, onOverview, readOnly }: {
  slot: CalendarSlot;
  minutes: number;               // live while resizing
  today: number;
  style: CSSProperties;
  onOpen: (s: CalendarSlot) => void;
  onRemove: (s: CalendarSlot) => void;
  onOverview: (projectId: number) => void;
  /** Someone else's calendar: no remove / resize handles. */
  readOnly?: boolean;
}) {
  const { t } = useT();
  const move = useDraggable({ id: `move:${slot.id}`, data: { kind: 'move', slot } satisfies DragData, disabled: slot.id < 0 });
  const resize = useDraggable({ id: `resize:${slot.id}`, data: { kind: 'resize', slot } satisfies DragData, disabled: slot.id < 0 });
  const start = minuteOfDay(new Date(slot.start_at));
  const state = slot.is_done ? 'done' : dueState(slot.due_date, today);
  const short = minutes <= STEP;
  return (
    <div
      ref={move.setNodeRef}
      className={`mycal-session is-${state}${slot.can_open ? '' : ' is-locked'}${short ? ' is-short' : ''}`}
      data-dragging={move.isDragging || undefined}
      style={style}
      title={`${slot.title ? `${slot.project_code ? `${slot.project_code} ` : ''}${slot.title}` : t('calendar.privateTask')} · ${hhmm(start)}–${hhmm(start + minutes)}${slot.project_title ? ` · ${slot.project_title}` : ''}`}
      {...move.listeners}
      {...move.attributes}
    >
      <span className="mycal-session-title">
        {slot.is_done ? <Check size={11} /> : !slot.can_open ? <Lock size={11} /> : state === 'overdue' ? <AlertTriangle size={11} /> : state === 'soon' ? <Hourglass size={11} /> : null}
        {slot.can_open
          ? <span className="mycal-title-link" onClick={(e) => { e.stopPropagation(); onOpen(slot); }}><Code code={slot.project_code} />{slot.title}</span>
          : <span>{t('calendar.privateTask')}</span>}
      </span>
      {!short && (
        <span className="mycal-session-meta">
          {hhmm(start)}–{hhmm(start + minutes)}
          {slot.project_title && slot.project_id != null
            ? <> · <span className="code-link" title={t('calendar.projectOverviewHelp')} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onOverview(slot.project_id!); }}>{slot.project_title}</span></>
            : slot.board_name ? <> · {slot.board_name}</> : null}
        </span>
      )}
      {!readOnly && <button
        type="button"
        className="mycal-session-remove"
        title={t('calendar.removeFromCalendar')}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove(slot); }}
      >
        <X size={12} />
      </button>}
      {/* The session's copy in the person's Outlook calendar. */}
      {!short && slot.outlook && (
        <span className={`mycal-session-outlook is-${slot.outlook}`} title={t(OUTLOOK_KEY[slot.outlook])}>
          {slot.outlook === 'synced' ? <CalendarCheck size={11} /> : <CalendarClock size={11} />}
        </span>
      )}
      {!readOnly && <div
        ref={resize.setNodeRef}
        className="mycal-session-resize"
        title={t('calendar.resize')}
        {...resize.attributes}
        onPointerDown={(e) => { e.stopPropagation(); resize.listeners?.onPointerDown?.(e); }}
      />}
    </div>
  );
}

// ── Outlook overlay (read-only, non-draggable) ──
function OutlookBlock({ ev, start, end, style }: { ev: OutlookEvent; start: number; end: number; style: CSSProperties }) {
  return (
    <div className="mycal-outlook" style={style} title={`${ev.subject} · ${hhmm(start)}–${hhmm(end)}${ev.location ? ` · ${ev.location}` : ''}`}>
      <span className="mycal-outlook-title">{ev.subject}</span>
      <span className="mycal-outlook-meta">Outlook · {hhmm(start)}–{hhmm(end)}</span>
    </div>
  );
}

// ── A task in the list under the week ──
// The same card as on the Kanban board: the title with the project code in
// front, the project it belongs to (opens the project overview), the due date
// and the card id. Draggable; it stays in the list once planned.
function TrayCardView({ task, today, sessions, onOpen, onOverview }: {
  task: PlannerTask;
  today: number;
  sessions: number;
  onOpen?: (t: PlannerTask) => void;
  onOverview?: (projectId: number) => void;
}) {
  const { t } = useT();
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const projectId = task.project_id;
  const overview = projectId != null && onOverview ? () => onOverview(projectId) : undefined;
  return (
    <div className={`task-card mycal-task-card is-${dueState(task.due_date, today)}`}>
      {overview && (
        <button type="button" className="project-card-info" title={t('calendar.projectOverviewHelp')} aria-label={t('calendar.projectOverview')} onPointerDown={stop} onClick={(e) => { stop(e); overview(); }}>
          <Eye size={14} />
        </button>
      )}
      <div className="task-card-title" onClick={(e) => { stop(e); onOpen?.(task); }}>
        <Code code={task.project_code} />{task.title}
      </div>
      {task.source === 'project' ? (
        <div
          className={`task-card-project${overview ? ' is-link' : ''}`}
          title={overview ? t('calendar.projectOverviewHelp') : undefined}
          onPointerDown={overview ? stop : undefined}
          onClick={overview ? (e) => { stop(e); overview(); } : undefined}
        >
          <span>{task.project_title ?? task.project_code}</span>
        </div>
      ) : (
        <div className="task-card-project"><LayoutGrid size={11} /><span>{task.board_name || t('calendar.kanbanBoard')}</span></div>
      )}
      <div className="task-card-meta">
        <div className="task-card-dates">
          <DueDate due={task.due_date} today={today} />
          {sessions > 0 && <span className="mycal-card-count" title={t('calendar.sessionsThisWeekHelp')}>{t('calendar.sessionsThisWeek', { n: sessions })}</span>}
        </div>
        <span className="card-id">{taskDisplayCode(task)}</span>
      </div>
    </div>
  );
}

function TrayCard(props: { task: PlannerTask; today: number; sessions: number; onOpen: (t: PlannerTask) => void; onOverview: (projectId: number) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `tray:${keyOf(props.task)}`,
    data: { kind: 'tray', task: props.task } satisfies DragData,
  });
  return (
    <div ref={setNodeRef} className="mycal-tray-item" data-dragging={isDragging || undefined} {...listeners} {...attributes}>
      <TrayCardView {...props} />
    </div>
  );
}

// ── Page ──
export function CalendarPage() {
  const { t } = useT();
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()));
  const [tasks, setTasks] = useState<PlannerTask[]>([]);
  const [slots, setSlots] = useState<CalendarSlot[]>([]);
  const [outlook, setOutlook] = useState<OutlookEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState<DragData | null>(null);
  const [overCell, setOverCell] = useState<{ day: number; minute: number } | null>(null);
  const [resizing, setResizing] = useState<{ id: number; minutes: number } | null>(null);
  const [openTask, setOpenTask] = useState<PlannerTask | null>(null);
  const [overviewProjectId, setOverviewProjectId] = useState<number | null>(null);
  const closeOverview = useCallback(() => setOverviewProjectId(null), []);
  const [outlookConfigured, setOutlookConfigured] = useState(false);
  const [now, setNow] = useState(() => new Date());
  // The PMO (admins) and PMO accompanists can open anyone's calendar, read-only:
  // the server answers the people list only to them (the picker stays hidden otherwise).
  const [calendarUsers, setCalendarUsers] = useState<{ id: number; name: string }[]>([]);
  const [viewUser, setViewUser] = useState<{ id: number; name: string } | null>(null);
  const readOnly = viewUser != null;
  useEffect(() => {
    (api.plannerCalendarUsers() as Promise<{ id: number; name: string }[]>)
      .then(setCalendarUsers)
      .catch(() => setCalendarUsers([]));
  }, []);

  // Whether sessions are copied to Outlook (Microsoft Graph set up by an admin) — for the hint under the week.
  useEffect(() => {
    (api.getCalendarStatus() as Promise<{ configured: boolean }>)
      .then((s) => setOutlookConfigured(!!s.configured))
      .catch(() => setOutlookConfigured(false));
  }, []);
  const scrollRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const noSensors = useSensors();

  // The "now" line moves with the clock.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const today = dayNum(localYmd(now));

  const loadTasks = useCallback(async () => {
    try {
      setTasks(await api.plannerMyTasks(viewUser?.id) as PlannerTask[]);
    } catch { setTasks([]); }
  }, [viewUser]);

  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);
  const loadSlots = useCallback(async () => {
    try {
      setSlots(await api.plannerSlots(weekStart.toISOString(), weekEnd.toISOString(), viewUser?.id) as CalendarSlot[]);
    } catch { setSlots([]); }
  }, [weekStart, weekEnd, viewUser]);

  useEffect(() => { loadTasks().then(() => setLoading(false)); }, [loadTasks]);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  // Changes made elsewhere — a copy deleted in Outlook removes its session —
  // show up without a reload: the week's sessions are re-read every minute and
  // when the tab comes back, never in the middle of a drag.
  const dragRef = useRef<DragData | null>(null);
  useEffect(() => { dragRef.current = drag; }, [drag]);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible' && !dragRef.current) loadSlots(); };
    const timer = window.setInterval(refresh, 60_000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [loadSlots]);

  // Pull the user's own Outlook events for the visible week (read-only) —
  // never someone else's: their PMO sessions are shown, not their meetings.
  useEffect(() => {
    if (viewUser) { setOutlook([]); return; }
    let cancelled = false;
    api.plannerOutlook(weekStart.toISOString(), weekEnd.toISOString())
      .then((data) => { if (!cancelled) setOutlook(data as OutlookEvent[]); })
      .catch(() => { if (!cancelled) setOutlook([]); });
    return () => { cancelled = true; };
  }, [weekStart, weekEnd, viewUser]);

  // Open on the working day: the current hour (or 08:00) near the top.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || loading) return;
    const m = minuteOfDay(new Date());
    const target = m >= DAY_START && m < DAY_END ? Math.max(DAY_START, m - 60) : 8 * 60;
    el.scrollTop = topOf(Math.floor(target / 60) * 60);
  }, [loading]);

  const days = useMemo(() => Array.from({ length: DAY_COUNT }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const dayIndexOf = useCallback((iso: string) => {
    const d = new Date(iso);
    return days.findIndex((x) => sameDay(x, d));
  }, [days]);

  const slotsByDay = useMemo<CalendarSlot[][]>(() => {
    const cols: CalendarSlot[][] = Array.from({ length: DAY_COUNT }, () => []);
    for (const s of slots) {
      const i = dayIndexOf(s.start_at);
      if (i >= 0) cols[i].push(s);
    }
    return cols;
  }, [slots, dayIndexOf]);

  const outlookByDay = useMemo(() => {
    const timed: OutlookEvent[][] = Array.from({ length: DAY_COUNT }, () => []);
    const allDay: OutlookEvent[][] = Array.from({ length: DAY_COUNT }, () => []);
    for (const e of outlook) {
      const i = dayIndexOf(e.start);
      if (i >= 0) (e.is_all_day ? allDay : timed)[i].push(e);
    }
    return { timed, allDay };
  }, [outlook, dayIndexOf]);

  const minutesOf = useCallback((s: CalendarSlot) => (resizing?.id === s.id ? resizing.minutes : s.minutes), [resizing]);

  const dayLayouts = useMemo<Map<string, EventLayout>[]>(() => days.map((_, i) => {
    const events: LayoutEvent[] = [];
    for (const ev of outlookByDay.timed[i]) {
      events.push({ id: `outlook-${ev.id}`, startMs: new Date(ev.start).getTime(), endMs: new Date(ev.end).getTime() });
    }
    for (const s of slotsByDay[i]) {
      const startMs = new Date(s.start_at).getTime();
      events.push({ id: `slot-${s.id}`, startMs, endMs: startMs + minutesOf(s) * 60000 });
    }
    return layoutDay(events);
  }), [days, outlookByDay, slotsByDay, minutesOf]);

  // Sessions per task in the week shown — on each card of the list.
  const sessionCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of slots) m.set(slotItemKey(s), (m.get(slotItemKey(s)) ?? 0) + 1);
    return m;
  }, [slots]);

  const groups = useMemo(() => {
    const by: Record<DueState, PlannerTask[]> = { overdue: [], soon: [], later: [] };
    for (const task of tasks) by[dueState(task.due_date, today)].push(task);
    return (['overdue', 'soon', 'later'] as DueState[]).map((state) => ({ state, tasks: by[state] })).filter((g) => g.tasks.length > 0);
  }, [tasks, today]);

  // ── Drag ──
  const onDragStart = (e: DragStartEvent) => setDrag((e.active.data.current as DragData) ?? null);
  const onDragOver = (e: DragOverEvent) => setOverCell(parseCell(e.over?.id));
  const onDragMove = (e: DragMoveEvent) => {
    const data = e.active.data.current as DragData | undefined;
    if (data?.kind !== 'resize') return;
    const start = minuteOfDay(new Date(data.slot.start_at));
    const raw = data.slot.minutes + (e.delta.y / HOUR_H) * 60;
    const minutes = Math.min(DAY_END - start, Math.max(STEP, Math.round(raw / STEP) * STEP));
    setResizing((r) => (r?.id === data.slot.id && r.minutes === minutes ? r : { id: data.slot.id, minutes }));
  };
  const onDragCancel = () => { setDrag(null); setOverCell(null); setResizing(null); };

  const onDragEnd = (e: DragEndEvent) => {
    const data = e.active.data.current as DragData | undefined;
    const target = parseCell(e.over?.id);
    const resized = resizing;
    onDragCancel();
    if (!data) return;

    if (data.kind === 'resize') {
      if (!resized || resized.minutes === data.slot.minutes) return;
      setSlots((prev) => prev.map((s) => (s.id === data.slot.id ? { ...s, minutes: resized.minutes } : s)));
      api.plannerUpdateSlot(data.slot.id, { minutes: resized.minutes }).catch(() => loadSlots());
      return;
    }
    if (!target) return;
    const dt = addDays(weekStart, target.day);
    dt.setHours(0, target.minute, 0, 0);
    const startISO = dt.toISOString();

    if (data.kind === 'move') {
      if (new Date(data.slot.start_at).getTime() === dt.getTime()) return;
      setSlots((prev) => prev.map((s) => (s.id === data.slot.id ? { ...s, start_at: startISO } : s)));
      api.plannerUpdateSlot(data.slot.id, { start_at: startISO }).catch(() => loadSlots());
      return;
    }

    // A new session of a task from the list — the task itself stays as it is.
    const task = data.task;
    const tempId = -Date.now();
    setSlots((prev) => [...prev, {
      id: tempId, source: task.source, item_id: task.id, start_at: startISO, minutes: DEFAULT_MINUTES,
      title: task.title, due_date: task.due_date, project_id: task.project_id, project_code: task.project_code,
      project_title: task.project_title, board_name: task.board_name, is_done: false, can_open: true, outlook: null,
    }]);
    api.plannerAddSlot(task.source, task.id, startISO, DEFAULT_MINUTES)
      .then((saved) => setSlots((prev) => prev.map((s) => (s.id === tempId ? saved as CalendarSlot : s))))
      .catch(() => loadSlots());
  };

  const removeSlot = (slot: CalendarSlot) => {
    setSlots((prev) => prev.filter((s) => s.id !== slot.id));
    api.plannerDeleteSlot(slot.id).catch(() => loadSlots());
  };

  const openSlot = (slot: CalendarSlot) => {
    const task = tasks.find((x) => x.source === slot.source && x.id === slot.item_id);
    if (task) setOpenTask(task);
    else if (slot.source === 'project') window.location.hash = `tasks?task=${slot.item_id}`;
  };

  if (loading) {
    return <div className="page"><div className="spinner" /></div>;
  }

  const todayIdx = days.findIndex((d) => sameDay(d, now));
  const nowMinute = minuteOfDay(now);
  const ghostMinutes = drag?.kind === 'move' ? drag.slot.minutes : DEFAULT_MINUTES;

  return (
    <div className="cal-page">
      {/* Week navigator — in the page toolbar, like every page's controls */}
      <div className="toolbar page-toolbar">
        <div className="mycal-weekbar">
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart((w) => addDays(w, -7))} title={t('calendar.prevWeek')} aria-label={t('calendar.prevWeek')}>
            <ChevronLeft size={15} />
          </button>
          <span className="mycal-weekbar-label">
            <span className="mycal-weekbar-num">{t('calendar.weekNum', { n: isoWeek(weekStart) })}</span>
            {fmtRange(weekStart, t)}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart((w) => addDays(w, 7))} title={t('calendar.nextWeek')} aria-label={t('calendar.nextWeek')}>
            <ChevronRight size={15} />
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(startOfWeek(new Date()))} title={t('calendar.jumpThisWeek')}>{t('calendar.thisWeek')}</button>
        </div>
        {calendarUsers.length > 0 && (
          <div className="mycal-person" title={t('calendar.personHelp')}>
            <SearchableSelect
              value={viewUser?.id ?? 0}
              options={[{ value: 0, label: t('calendar.mine') }, ...calendarUsers.map((u) => ({ value: u.id, label: u.name }))]}
              onChange={(v) => setViewUser(calendarUsers.find((x) => x.id === Number(v)) ?? null)}
            />
          </div>
        )}
        <div className="tlt-legend mycal-legend">
          <span className="tlt-legend-item"><span className="mycal-swatch is-later" />{t('calendar.legend.session')}</span>
          <span className="tlt-legend-item"><span className="mycal-swatch is-soon" />{t('calendar.legend.soon')}</span>
          <span className="tlt-legend-item"><span className="mycal-swatch is-overdue" />{t('calendar.legend.overdue')}</span>
          <span className="tlt-legend-item"><span className="mycal-swatch is-done" />{t('calendar.legend.done')}</span>
          <span className="tlt-legend-item"><span className="mycal-swatch is-outlook" />{t('calendar.legend.outlook')}</span>
        </div>
      </div>

      <DndContext sensors={readOnly ? noSensors : sensors} collisionDetection={collision} onDragStart={onDragStart} onDragOver={onDragOver} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        {/* Work-week grid */}
        <div className="mycal-board">
          <div className="mycal-scroll" ref={scrollRef}>
            <div className="mycal-grid">
              <div className="mycal-corner" />
              {days.map((date, i) => (
                <div key={i} className={`mycal-head${i === todayIdx ? ' is-today' : ''}${dayNum(localYmd(date)) < today ? ' is-past' : ''}`}>
                  <span className="mycal-head-name">{t(DAY_KEYS[i])}</span>
                  <span className="mycal-head-date">{date.getDate()}</span>
                  {outlookByDay.allDay[i].map((ev) => (
                    <span key={ev.id} className="mycal-head-allday" title={ev.subject}>{ev.subject}</span>
                  ))}
                </div>
              ))}

              <div className="mycal-gutter" style={{ height: GRID_H }}>
                {HOURS.map((h) => (
                  <span key={h} className="mycal-gutter-hour" style={{ top: topOf(h * 60) }}>{String(h).padStart(2, '0')}:00</span>
                ))}
              </div>

              {days.map((date, dayIdx) => (
                <div
                  key={dayIdx}
                  className={`mycal-day${dayIdx === todayIdx ? ' is-today' : ''}${dayNum(localYmd(date)) < today ? ' is-past' : ''}`}
                  style={{ height: GRID_H }}
                >
                  {CELL_MINUTES.map((m) => <Cell key={m} day={dayIdx} minute={m} />)}

                  {outlookByDay.timed[dayIdx].map((ev) => {
                    // Shown within the visible hours (a meeting ending another day runs to the bottom).
                    const startD = new Date(ev.start), endD = new Date(ev.end);
                    const start = minuteOfDay(startD);
                    const end = sameDay(startD, endD) ? minuteOfDay(endD) : DAY_END;
                    const from = Math.max(start, DAY_START), to = Math.min(end, DAY_END);
                    if (to <= from) return null;
                    return <OutlookBlock key={ev.id} ev={ev} start={start} end={end} style={blockStyle(dayLayouts[dayIdx].get(`outlook-${ev.id}`), topOf(from), heightOf(from, to - from))} />;
                  })}

                  {slotsByDay[dayIdx].map((s) => {
                    const start = minuteOfDay(new Date(s.start_at));
                    if (start < DAY_START || start >= DAY_END) return null;
                    const minutes = minutesOf(s);
                    return (
                      <SessionBlock
                        key={s.id}
                        slot={s}
                        minutes={minutes}
                        today={today}
                        style={blockStyle(dayLayouts[dayIdx].get(`slot-${s.id}`), topOf(start), heightOf(start, minutes))}
                        onOpen={openSlot}
                        onRemove={removeSlot}
                        onOverview={setOverviewProjectId}
                        readOnly={readOnly}
                      />
                    );
                  })}

                  {/* Where the dragged task or session will land. */}
                  {overCell?.day === dayIdx && drag && drag.kind !== 'resize' && (
                    <div className="mycal-ghost" style={{ top: topOf(overCell.minute), height: heightOf(overCell.minute, ghostMinutes) }}>
                      {hhmm(overCell.minute)}–{hhmm(Math.min(DAY_END, overCell.minute + ghostMinutes))}
                    </div>
                  )}

                  {dayIdx === todayIdx && nowMinute >= DAY_START && nowMinute < DAY_END && (
                    <div className="mycal-now" style={{ top: topOf(nowMinute) }} title={t('planning.today')} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* My open tasks, under the week — dragged in as many times as needed. */}
        <section className="mycal-tray">
          <div className="mycal-tray-head">
            <span className="mycal-tray-title">{viewUser ? t('calendar.tasksOf', { name: viewUser.name }) : t('calendar.myTasks')}</span>
            <span className="mycal-tray-count">{tasks.length}</span>
            <span className="mycal-tray-hint">
              {viewUser ? t('calendar.readOnlyHint', { name: viewUser.name }) : <>{t('calendar.trayHint')}{outlookConfigured && <> {t('calendar.outlookHint')}</>}</>}
            </span>
          </div>
          {tasks.length === 0 ? (
            <div className="mycal-tray-empty">{viewUser ? t('calendar.trayEmptyOther', { name: viewUser.name }) : t('calendar.trayEmpty')}</div>
          ) : groups.map((g) => (
            <div key={g.state} className={`mycal-group is-${g.state}`}>
              <div className="mycal-group-title">{t(GROUP_KEY[g.state])} <span className="mycal-group-count">{g.tasks.length}</span></div>
              <div className="mycal-cards">
                {g.tasks.map((task) => (
                  <TrayCard key={keyOf(task)} task={task} today={today} sessions={sessionCounts.get(keyOf(task)) ?? 0} onOpen={setOpenTask} onOverview={setOverviewProjectId} />
                ))}
              </div>
            </div>
          ))}
        </section>

        <DragOverlay dropAnimation={null}>
          {drag?.kind === 'tray' && (
            <div className="mycal-tray-item" data-dnd-overlay>
              <TrayCardView task={drag.task} today={today} sessions={sessionCounts.get(keyOf(drag.task)) ?? 0} />
            </div>
          )}
          {drag?.kind === 'move' && (
            <div className={`mycal-session mycal-session--overlay is-${drag.slot.is_done ? 'done' : dueState(drag.slot.due_date, today)}`}>
              <span className="mycal-session-title">
                {drag.slot.title ? <><Code code={drag.slot.project_code} />{drag.slot.title}</> : t('calendar.privateTask')}
              </span>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {openTask && (
        <TaskDetailModal
          task={openTask}
          onClose={() => setOpenTask(null)}
          onSaved={() => { setOpenTask(null); loadTasks(); loadSlots(); }}
        />
      )}

      {overviewProjectId != null && (
        <ProjectOverviewModal projectId={overviewProjectId} onClose={closeOverview} />
      )}
    </div>
  );
}
